import React, { useState, useEffect, memo, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import ReportIncidenService from "../../components/api/ReportIncidenService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { successNotification, errorNotification } from "src/store/actions";
import { toast } from "react-toastify";
import { Button, Col, Row, UncontrolledTooltip } from "reactstrap";
import {
    HiChevronDoubleUp,
    HiChevronDoubleDown,
    HiMenuAlt4,
} from "react-icons/hi";
import DeleteTaskModal from "src/modules/data-protection/components/RisksAndTasks/tasks/delete-modal";
import CreateTaskModal from "src/modules/data-protection/components/RisksAndTasks/tasks/new";
import DateUtils from "src/services/utils/DateUtils";
import Spinner from "react-bootstrap/Spinner";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import TaskManagerService from "src/modules/data-protection/apis/TaskManagerService";
import { withRouter, useLocation } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { ControlsFrameworks, formatTaskAndRiskId, TasksStatuses } from "src/modules/data-protection/constants/Common";
import FilteringComponent from "./components/filtering-component";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";

const TaskManagmentTab = (props) => {
    const location = useLocation();
    const { t, caseId, onTaskSelected, isReportDeleted, incidentId } = props;

    const ismsControlsOptions = {
        incident_id: incidentId
    }

    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        status: null,
        priority: null,
        owner: null,
    });
    const [deleteTaskId, setDeleteTaskId] = useState(null);
    const [owners, setOwners] = useState([]);

    const [createTaskModalStatus, setCreateTaskModalStatus] = useState(false);
    const [deleteTaskModalStatus, setDeleteTaskModalStatus] = useState(false);

    const dateUtils = new DateUtils();

    const handleFilterOnChange = useCallback((newFilters) => {
        setTableFilters((currentStatue) => {
            return {
                ...currentStatue,
                ...newFilters
            }
        });
    }, []);

    const handleFetchTasksQuery = useQuery({
        queryKey: [
            "gdpr-fetch-incident-tasks", 
            tableFilters
        ],
        queryFn: async () => {
            const service = ReportIncidenService.getInstance();
            return await service.fetchTasks(caseId, tableFilters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching tasks."), {
                type: "error",
            });
        }
    });

    const ismsAccessibleControlsQuery = useQuery({
        queryKey: [
            'gdpr-fetch-isms-accessible-controls',
            ismsControlsOptions
        ],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchAccessibleControls({
                ...ismsControlsOptions
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while retrieving the controls list.'), {
                type: 'error'
            });
        }
    });

    const handleFetchTasksOwners = useQuery({
        queryKey: ["gdpr-fetch-incident-tasks-owners"],
        queryFn: async () => {
            const service = TaskManagerService.getInstance();

            return await service.fetchOwners(caseId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            toast(t("An error occurred while fetching owners."), {
                type: "error",
            });
        },
    });

    const handleDeleteTaskMutation = useMutation({
        mutationFn: async (taskId) => {
            const service = TaskManagerService.getInstance();

            return await service.deleteTask(caseId, taskId);
        },
        onSuccess: () => {
            setDeleteTaskModalStatus(false);

            handleFetchTasksQuery.refetch();

            toast(t("Task deleted successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("Failed to delete task."), {
                type: "error",
            });
        }
    });

    const priorities = useMemo(() => {
        return [
            {
                value: "low",
                baseLabel: "Low",
                label: (
                    <>
                        <HiChevronDoubleDown className="text-info me-1" />
                        <span>
                            {t("Low")}
                        </span>
                    </>
                ),
            },
            {
                value: "medium",
                baseLabel: "Medium",
                label: (
                    <>
                        <HiMenuAlt4 className="text-success me-1" />
                        <span>
                            {t("Medium")}
                        </span>
                    </>
                ),
            },
            {
                value: "high",
                baseLabel: "High",
                label: (
                    <>
                        <HiChevronDoubleUp className="text-danger me-1" />
                        <span>
                            {t("High")}
                        </span>
                    </>
                ),
            },
        ];
    }, [t]);

    const statuses = useMemo(() => {
        return [
            { value: "NEW", label: t("New"), baseLabel: "New" },
            {
                value: "IN_PROGRESS",
                label: t("In Progress"),
                baseLabel: "In Progress",
            },
            { value: "REVIEW", label: t("Review"), baseLabel: "Review" },
            { value: "CLOSED", label: t("Closed"), baseLabel: "Closed" },
            { value: "DELETED", label: t("Deleted"), baseLabel: "Deleted" },
        ];
    }, [t]);

    useEffect(() => {
        if (handleFetchTasksOwners.data) {
            setOwners(
                handleFetchTasksOwners.data.map((item) => {
                    return {
                        value: item.id,
                        label: `${item.first_name} ${item.last_name}`,
                    };
                })
            );
        }
    }, [handleFetchTasksOwners.data]);

    useEffect(() => {
        const taskId = new URLSearchParams(location.search).get("taskId");
        
        if (taskId && owners.length > 0) {
          taskSelectedToShow(taskId);
        }
      }, [
        owners,
        location.search
    ]);

    const taskSelectedToShow = (id) => {
        onTaskSelected && onTaskSelected({
            taskId: id
        });
    };

    const renderModals = () => {
        return (
            <React.Fragment>
                <CreateTaskModal
                    isOpen={createTaskModalStatus}
                    closeModal={() => setCreateTaskModalStatus(false)}
                    owners={owners}
                    initialTaskStatus={{ value: 'NEW', label: t('New') }}
                    priorities={ priorities }
                    successCreation={() => handleFetchTasksQuery.refetch()}
                    creationRequestPayload={{
                        reportCaseId: caseId
                    }}
                    ismsControlsOptions={ismsControlsOptions}
                />

                <DeleteTaskModal
                    isOpen={deleteTaskModalStatus}
                    taskId={deleteTaskId}
                    close={() => {
                        setDeleteTaskId(null);
                        setDeleteTaskModalStatus(false);
                    }}
                    acceptDeletion={(id) => {
                        handleDeleteTaskMutation.mutate(id);
                    }}
                />
            </React.Fragment>
        );
    };

    const handleRenderReportStatus = (statusId) => {
        const taskStatus = TasksStatuses[statusId];

        return (
            <div className='dt-task-status text-dark' style={{
                backgroundColor: `${taskStatus.color}`
            }}>
                {t(taskStatus.title)}
            </div>
        );
    };

    const {
        accessible_frameworks = []
    } = ismsAccessibleControlsQuery.data || {};

    const controlsColumns = accessible_frameworks?.map((accessibleFramework, index) => {
        const controleInfos = ControlsFrameworks.find((item) => item.key === accessibleFramework);

        if (!controleInfos) return undefined;

        return {
            key: (9 + index),
            dataField: "controls",
            text: props.t(controleInfos.title),
            style: {
                width: '160px'
            },
            formatter: (cellContent) => {
                return (
                    <div>
                        {(cellContent[accessibleFramework] || []).map((c) => formatControlNumber(c.control_number, accessibleFramework)).join(', ')}
                    </div>
                )
            },
            sort: false
        }
    });

    const tableColumns = [
        {
            key: 1,
            dataField: "id",
            text: props.t("Task ID"),
            formatter: (cellContent, row) => {
                return (
                    <div>
                        <div
                            className="dt-task-id"
                            style={{
                                cursor: !row.deleted_at ? "pointer" : "default",
                            }}
                            onClick={() => !row.deleted_at && taskSelectedToShow(row.id)}
                        >
                            {formatTaskAndRiskId(
                                "task",
                                row?.questionnaire === "software"
                                    ? row?.asset_id
                                    : row?.case_secondary_id,
                                row?.risk_secondary_id ?? row.secondary_id,
                                row?.is_mitigation,
                                row?.questionnaire,
                                row.secondary_id
                            )}
                        </div>{" "}
                    </div>
                );
            },
            style: {
                width: '120px'
            },
            sort: true,
        },
        {
            key: 2,
            dataField: "name",
            text: props.t("Task"),
            style     :   {
                width : '140px'
            },
            formatter: (cellContent, { 
                id, 
                is_recurring, 
                recurrence_secondary_id,
                deleted_at
            }) => {
                return (
                    <div className="d-flex align-items-center">
                        {is_recurring && recurrence_secondary_id && (
                            <>
                                <i
                                    className="ri-repeat-2-line text-info me-1 font-size-18 align-middle"
                                    id={`recurring-task-${id}-tooltip`}
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                />
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`recurring-task-${id}-tooltip`}
                                >
                                    {t('Recurring task')}
                                </UncontrolledTooltip>
                            </>
                        )}
                        <span
                            className={`${deleted_at ? "" : "hoverable"}`}
                            style={{
                                cursor: !deleted_at ? "pointer" : "default",
                            }}
                            onClick={() => !deleted_at && taskSelectedToShow(id)}
                        >
                            {cellContent}{recurrence_secondary_id ? `-${recurrence_secondary_id}` : ''}
                        </span>
                    </div>
                );
            },
            sort: true,
        },
        {
            key: 3,
            dataField: "createdAt",
            text: props.t("Creation Date"),
            style: {
                width: '120px'
            },
            formatter: (_cellContent, { created_at }) => {
                return (
                    <div>
                        <div className="dt-task-creation-date">
                            {dateUtils.convertTimeStampToDate(
                                created_at,
                                INTERNATIONAL_DATE_FORMAT
                            )}
                        </div>
                    </div>
                );
            },
            sort: true,
            headerStyle: { whiteSpace: "nowrap" },
        },
        {
            key: 4,
            dataField: "endedAt",
            text: props.t("Deadline"),
            style: {
                width: '120px'
            },
            formatter: (_cellContent, { ended_at }) => {
                return (
                    <div>
                        <div className="dt-task-deadline-date">
                            {dateUtils.convertTimeStampToDate(
                                ended_at,
                                INTERNATIONAL_DATE_FORMAT
                            )}
                        </div>
                    </div>
                );
            },
            sort: true,
        },
        {
            key: 5,
            dataField: "manager",
            text: props.t("Task Manager"),
            style: {
                width: '180px'
            },
            formatter: (cellContent, row) => {
                if (!cellContent) return null;
                return (
                    <div className="d-flex gap-1 align-items-center">
                        <span className="dt-list-col-bullet">{row.manager[0]}</span>{" "}
                        <span className="dt-list-col-bullet-text">{row.manager}</span>
                    </div>
                );
            },
            sort: true,
            headerStyle: { whiteSpace: "nowrap" },
        },
        {
            key: 6,
            dataField: "analyst",
            text: props.t("Task Owner"),
            style: {
                width: '180px'
            },
            formatter: (cellContent) => {
                if (!cellContent) return null;
                return (
                    <div className="d-flex gap-1 align-items-center">
                        <span className="dt-list-col-bullet">
                            {cellContent?.full_name[0]}
                        </span>{" "}
                        <span className="dt-list-col-bullet-text">
                            {cellContent?.full_name}
                        </span>
                    </div>
                );
            },
            sort: true,
        },
        {
            key: 7,
            dataField: "priority",
            text: props.t("Priority"),
            style: {
                width: '100px'
            },
            formatter: (cellContent) => {
                const priority = cellContent?.toLowerCase();

                return (
                    <div className="dt-task-priority">
                        {priority === "high" ? (
                            <HiChevronDoubleUp className="text-danger" />
                        ) : priority === "medium" ? (
                            <HiMenuAlt4 className="text-success" />
                        ) : (
                            <HiChevronDoubleDown className="text-info" />
                        )}
                        <span style={{ marginLeft: "4px" }}>{t(cellContent)}</span>
                    </div>
                );
            },
            sort: true,
        },
        {
            key: 8,
            dataField: "status",
            text: props.t("Status"),
            formatter: (cellContent) => {
                return <div>{handleRenderReportStatus(cellContent)}</div>;
            },
            style: {
                width: '100px'
            },
            sort: true,
        },
        ...controlsColumns,
        {
            key: 11,
            dataField: "deletedAt",
            text: props.t("Deletion date"),
            style: {
                width: '120px'
            },
            formatter: (cellContent, {
                deleted_at
            }) => {
                return (
                    <div>
                        {deleted_at
                            ? dateUtils.convertTimeStampToDate(
                                deleted_at,
                                INTERNATIONAL_DATE_FORMAT
                            )
                            : null}
                    </div>
                );
            },
            sort: true,
            headerStyle: { whiteSpace: "nowrap" },
        },
        {
            key: 12,
            dataField: "actions",
            text: t("Action"),
            style: {
                width: '120px'
            },
            formatter: (cellContent, row) => {
                return (
                    <div>
                        {!row.deleted_at ? (
                            <div
                                style={{
                                    fontSize: 20,
                                    display: "block",
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setDeleteTaskId(row.id);
                                    setDeleteTaskModalStatus(true);
                                }}
                            >
                                <i className="ri-delete-bin-line text-danger"></i>
                            </div>
                        ) : (
                            <div
                                style={{
                                    fontWeight: 300,
                                    display: "block",
                                    color: "red",
                                }}
                            >
                                {t("Deleted")}
                            </div>
                        )}
                    </div>
                );
            },
        },
    ];

    const NoDataIndication = () =>
        handleFetchTasksQuery.isFetched &&
        !handleFetchTasksQuery.length ? (
        <div className="alert m-0" role="alert">
            <p
            style={{
                textAlign: "center",
                marginBottom: 0,
            }}
            >
            {props.t(EMPTY_LIST)}
            </p>
        </div>
        ) : (
        <></>
        );

    const handleTableChange = (type, {
        page,
        sizePerPage,

        sortField,
        sortOrder
    }) => {
        switch (type) {
            case "pagination":
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;
            case 'sort':
                setTableFilters({
                    ...tableFilters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    };

    return (
        <div className="p-4" style={{ marginBottom: "300px" }}>
            {renderModals()}
            <PaginationProvider
                pagination={paginationFactory({
                    custom: true,
                    page: tableFilters.pageIndex,
                    sizePerPage: tableFilters.pageSize,
                    totalSize: handleFetchTasksQuery.data?.itemsCount || 0,
                    withFirstAndLast: false,
                    alwaysShowAllBtns: true,
                    prePageText: (
                        <span>
                            <i className="ri-arrow-left-s-line"></i> {props.t("Back")}
                        </span>
                    ),
                    nextPageText: (
                        <span>
                            {props.t("Next")} <i className="ri-arrow-right-s-line"></i>
                        </span>
                    ),
                    prePageTitle: props.t("Pre page"),
                    firstPageTitle: props.t("Next page"),
                    showTotal: true,
                    hideSizePerPage: false,
                    sizePerPageList: [
                        {
                            text: "25",
                            value: 25,
                        },
                        {
                            text: "50",
                            value: 50
                        }
                    ]
                })}
            >
                {({ paginationProps, paginationTableProps }) => {
                    return (
                        <React.Fragment>
                            <Row className="mb-5 mt-3">
                                <Col sm="12">
                                    <div className="d-flex gap-2 justify-content-end">
                                        <FilteringComponent 
                                            onChange={handleFilterOnChange} 
                                            owners={handleFetchTasksOwners}
                                            statuses={statuses}
                                            priorities={priorities}
                                        />

                                        <Button
                                            color="primary"
                                            style={{
                                                display: "block",
                                                height: "max-content",
                                            }}
                                            onClick={() => setCreateTaskModalStatus(true)}
                                            outline
                                            disabled={isReportDeleted}
                                        >
                                            {t("Create Task")}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            
                            <Row>
                                <Col sm="12">
                                    <BootstrapTable
                                        remote={{
                                            pagination: true,
                                            filter: false,
                                            sort: true,
                                            cellEdit: false,
                                            search: false,
                                        }}
                                        loading={
                                            handleFetchTasksQuery.isFetching ||
                                            handleFetchTasksQuery.isLoading ||
                                            handleFetchTasksOwners.isLoading ||
                                            handleFetchTasksOwners.isFetching
                                        }
                                        overlay={overlayFactory({
                                            spinner: (
                                                <Spinner
                                                    animation="border"
                                                    variant="primary"
                                                    size="md"
                                                />
                                            ),
                                            text: "Loading...",
                                        })}
                                        onTableChange={handleTableChange}
                                        defaultSorted={[]}
                                        keyField={"id"}
                                        responsive
                                        bordered={false}
                                        data={handleFetchTasksQuery.data?.tasks || []}
                                        striped={true}
                                        columns={tableColumns}
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table incident-table"}
                                        headerWrapperClasses={"thead-light"}
                                        style={{
                                            overflowX: "auto",
                                        }}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationListStandalone {...paginationProps} />

                                        <SizePerPageDropdownStandalone {...paginationProps} />
                                    </div>
                                </Col>
                            </Row>
                        </React.Fragment>
                    );
                }}
            </PaginationProvider>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    const { Organization } = state;
    return {
        user: state.Login.user,
        token,
        organization: Organization,
    };
};

export default withNamespaces()(
  withRouter(
    connect(mapStatetoProps, {
      successNotification,
      errorNotification,
    })(memo(TaskManagmentTab))
  )
);
